// Audit matrix (Dean, 13 Oct 2026) — every live file counted by combination; each combination has a rule → one-click fix or review.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Grid3x3, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getLegalAuditMatrix, downloadLegalAuditMatrixCsv, getLegalProcessMe } from "@/lib/api";
import { AuditCombinations } from "./AuditCombinations";
import { AuditFiles } from "./AuditFiles";
import { AuditAirtable } from "./AuditAirtable";
import { AuditFixDialog } from "./AuditFixDialog";
import { LegalDeskBanner, useLegalDesk } from "../legalDesk";

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function Totals({ t }) {
  const cells = [
    ["files", "files", "text-slate-900"], ["ok", "consistent", "text-emerald-700"], ["fix", "one-click fix", "text-rose-700"], ["review", "to review", "text-amber-700"],
    ["combinations", "combinations", "text-indigo-900"], ["on_worklist", "on worklist", "text-slate-700"], ["on_tracker", "on tracker", "text-slate-700"], ["both", "on both", "text-slate-700"],
  ];
  return (
    <div className="flex items-center gap-4 flex-wrap text-[11px] text-muted-foreground" data-testid="audit-totals">
      {cells.map(([k, label, tone]) => (
        <span key={k} className="whitespace-nowrap"><span className={`font-semibold tabular-nums ${tone}`} data-testid={`audit-total-${k}`}>{t?.[k] ?? 0}</span> <span className="ml-1">{label}</span></span>
      ))}
    </div>
  );
}

export default function LegalAuditMatrix() {
  const navigate = useNavigate();
  useLegalDesk();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);     // combination key
  const [status, setStatus] = useState("all");        // all | fix | review | ok
  const [fixDialog, setFixDialog] = useState(null);   // {fix, label, caseIds}
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setMatrix(await getLegalAuditMatrix()); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); getLegalProcessMe().then(setMe).catch(() => setMe({ can_publish: false })); }, [load]);

  const combos = useMemo(() => (matrix?.combinations || []).filter(c => status === "all" || c.status === status), [matrix, status]);
  const rows = useMemo(() => {
    let r = matrix?.rows || [];
    if (selected) r = r.filter(x => x.combo === selected);
    else if (status !== "all") r = r.filter(x => x.status === status);
    return r;
  }, [matrix, selected, status]);
  const selectedCombo = useMemo(() => (matrix?.combinations || []).find(c => c.key === selected) || null, [matrix, selected]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const blob = await downloadLegalAuditMatrixCsv();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `legal-audit-matrix-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Audit matrix CSV downloaded");
    } catch (e) { toast.error("CSV download failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="p-4 space-y-3 max-w-[1600px] mx-auto" data-testid="legal-audit-matrix">
      <LegalDeskBanner />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => navigate("/legal/case-tracker")} data-testid="audit-back"><ArrowLeft className="w-4 h-4" /></Button>
          <Grid3x3 className="w-5 h-5 text-red-700 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base font-semibold whitespace-nowrap">Audit matrix</h1>
            <p className="text-[11px] text-muted-foreground truncate">LoanDisk status × account × legal action × owner × diary × lists — one rule per combination. Portal reads LoanDisk; nothing is pulled back from Airtable.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {matrix?.generated_at && <span className="text-[10px] text-muted-foreground" data-testid="audit-generated-at">generated {new Date(matrix.generated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={load} disabled={loading} data-testid="audit-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Refresh
          </Button>
          <Button size="sm" className="h-8 text-xs bg-red-700 hover:bg-red-800 text-white" onClick={downloadCsv} disabled={downloading || !matrix} data-testid="audit-download-csv">
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Download CSV
          </Button>
        </div>
      </div>

      {error && <div className="border border-rose-200 bg-rose-50 rounded p-2 text-xs text-rose-800" data-testid="audit-error">{error}</div>}
      {loading && !matrix ? (
        <div className="py-16 flex justify-center" data-testid="audit-loading"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : matrix && (
        <>
          <Totals t={matrix.totals} />
          <AuditCombinations
            combos={combos} status={status} onStatus={(s) => { setStatus(s); setSelected(null); }}
            selected={selected} onSelect={(k) => setSelected(k === selected ? null : k)}
            canPublish={!!me?.can_publish} fmtGBP={fmtGBP}
            onFix={(c) => setFixDialog({ fix: c.fix, label: c.fix_label || matrix.fix_labels?.[c.fix] || c.fix, caseIds: c.case_ids, rule: c.rule })}
          />
          <AuditFiles rows={rows} total={(matrix.rows || []).length} selectedCombo={selectedCombo} onClearCombo={() => setSelected(null)} fmtGBP={fmtGBP}
            onOpen={(r) => navigate(`/legal/case/${r.case_id}?source=legal`)} />
          <AuditAirtable canPublish={!!me?.can_publish} />
        </>
      )}
      <AuditFixDialog req={fixDialog} onClose={() => setFixDialog(null)} onDone={() => { setFixDialog(null); load(); }} />
    </div>
  );
}
